import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import ModalOverlay from '../../components/ui/ModalOverlay';
import Sheet from '../../components/ui/Sheet';
import { ICON_MAP } from '../../components/ui/SearchCommandPalette';
import useIsMobile from '../../hooks/useIsMobile';
import useModalFocus from '../../hooks/useModalFocus';
import useScrollLock from '../../hooks/useScrollLock';
import { useAssistantParser } from '../../hooks/useAssistantParser';
import { SEARCH_COPY as COPY } from '../../constants/search_messages.fr';
import { RESULT_KIND, highlightParts, loadRecents, paletteGroups, pushRecent } from './searchModel';
import styles from './CommandPalette.module.css';

const LIST_ID = 'command-palette-list';

/** Names and pages only: a command proposal repeats the query, highlighting it is noise. */
function Highlighted({ text, query, enabled }) {
  if (!enabled) return text;
  return highlightParts(text, query).map((part, index) => (
    part.hit ? <mark key={index} className={styles.hit}>{part.text}</mark> : <span key={index}>{part.text}</span>
  ));
}

/** Input + grouped results; ↑ ↓ move, Enter runs, Esc closes (useModalFocus). */
function PaletteBody({ students, families, stats, onRun, onClose }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(loadRecents);
  const inputRef = useRef(null);
  const proposals = useAssistantParser(query, families, students, stats);
  const groups = useMemo(
    () => paletteGroups({ query, proposals, students, families, recents }),
    [query, proposals, students, families, recents],
  );
  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setActive(0); }, [query]);

  const pick = (item) => {
    if (item.recent) setRecents(pushRecent(item.recent));
    onClose();
    onRun(item.proposal);
  };

  const onKeyDown = (event) => {
    if (!flat.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pick(flat[active]);
    }
  };

  let index = -1;
  return (
    <>
      <div className={styles.field}>
        <Search size={20} aria-hidden="true" className={styles.searchIcon} />
        <input
          ref={inputRef}
          className={styles.input}
          type="search"
          enterKeyHint="search"
          role="combobox"
          aria-expanded="true"
          aria-controls={LIST_ID}
          aria-activedescendant={flat[active] ? `palette-${flat[active].key}` : undefined}
          aria-label={COPY.inputLabel}
          placeholder={COPY.placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className={styles.close} aria-label={COPY.close} onClick={onClose}><X size={20} /></button>
      </div>
      <div id={LIST_ID} className={styles.results} role="listbox" aria-label={COPY.dialogLabel}>
        {groups.map((group) => (
          <div key={group.id} role="group" aria-label={group.label}>
            <div className={styles.groupLabel} aria-hidden="true">{group.label}</div>
            {group.items.map((item) => {
              index += 1;
              const Icon = ICON_MAP[item.icon] || Search;
              const selected = index === active;
              const position = index;
              return (
                <div key={item.key} id={`palette-${item.key}`} role="option" aria-selected={selected}
                  className={`${styles.item}${selected ? ` ${styles.active}` : ''}`}
                  onMouseEnter={() => setActive(position)} onClick={() => pick(item)}>
                  <span className={styles.icon} aria-hidden="true"><Icon size={16} /></span>
                  <span className={styles.text}>
                    <span className={styles.label}>
                      <Highlighted text={item.label} query={query} enabled={item.kind !== RESULT_KIND.ACTION} />
                    </span>
                    {item.sub ? (
                      <span className={styles.sub}>
                        <Highlighted text={item.sub} query={query} enabled={item.kind !== RESULT_KIND.ACTION} />
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
        {!flat.length ? <p className={styles.empty}>{COPY.noResult}</p> : null}
      </div>
    </>
  );
}

function DesktopDialog({ onClose, children }) {
  const panelRef = useRef(null);
  useModalFocus(panelRef, onClose, true);
  useScrollLock(true);
  return (
    <ModalOverlay onClose={onClose} className={styles.overlay}>
      <section ref={panelRef} className={styles.dialog} role="dialog" aria-modal="true" aria-label={COPY.dialogLabel} tabIndex={-1}>
        {children}
      </section>
    </ModalOverlay>
  );
}

/** Ctrl/⌘K palette: centered dialog on desktop, full-screen Sheet on mobile. */
export default function CommandPalette({ onClose, ...props }) {
  const isMobile = useIsMobile();
  const body = <PaletteBody {...props} onClose={onClose} />;
  return isMobile
    ? <Sheet open full onClose={onClose} label={COPY.dialogLabel}>{body}</Sheet>
    : <DesktopDialog onClose={onClose}>{body}</DesktopDialog>;
}
